import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { speakText } from '../utils/speak';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import LunaLoader from '../LunaLoader';
import { lessonsEnglish } from '../data/lessonsEnglish';
import { lessonsItalian } from '../data/lessonsItalian';
import { useNavigate, useParams } from 'react-router-dom';
import './Lesson.css';
import { awardXP } from '../utils/xp';

const lessonVersions = {
};

const TEXT = {
    en: {
        reviewTitle: 'Quick review',
        reviewOne: 'Let’s go back over the question you missed. You’ll need to get it right before this lesson unlocks the next one.',
        reviewMany: 'Let’s go back over the {n} questions you missed. You’ll need to get every one of them right before this lesson unlocks the next one.',
        reviewQuestion: 'Review question {n} of {total}',
        question: 'Question {n}',
        saveTitle: 'Save to Word Bank',
        hint: 'Tap 🔖 or press and hold to save',
        hear: '▶ Hear it',
        saveAs: 'Save "{word}" as...',
        saveWord: '💾 Save word',
        savePhrase: '📝 Save phrase',
        cancel: 'Cancel',
        savedWord: '✓ Saved as a word!',
        savedPhrase: '✓ Saved as a phrase!',
        choose: 'Choose an answer',
        correct: 'Correct!',
        notQuite: 'Not quite. You’ll see this one again.',
        xpEarned: 'XP earned',
        correctLabel: 'Correct',
        questionsLabel: 'Questions',
        back: 'Back to topic →',
        continue: 'Continue →',
        startReview: 'Start review →',
        next: 'Next →',
        leaveTitle: 'Leave this lesson?',
        leaveDesc: 'Your progress in this lesson will be lost.',
        keepGoing: 'Keep going',
        leave: 'Leave',
        unavailableTitle: 'Temporarily unavailable',
        unavailableBody: 'This lesson couldn’t load right now. Please check your connection and try again.',
        tryAgainTitle: 'Please try again',
        tryAgainBody: 'We couldn’t load this lesson. Go back and try starting it again.',
    },
    it: {
        reviewTitle: 'Ripasso veloce',
        reviewOne: 'Ripassiamo la domanda che hai sbagliato. Dovrai rispondere correttamente per sbloccare la lezione successiva.',
        reviewMany: 'Ripassiamo le {n} domande che hai sbagliato. Dovrai rispondere correttamente a tutte per sbloccare la lezione successiva.',
        reviewQuestion: 'Domanda di ripasso {n} di {total}',
        question: 'Domanda {n}',
        saveTitle: 'Salva nel Word Bank',
        hint: 'Tocca 🔖 o tieni premuto per salvare',
        hear: '▶ Ascolta',
        saveAs: 'Salva "{word}" come...',
        saveWord: '💾 Salva parola',
        savePhrase: '📝 Salva frase',
        cancel: 'Annulla',
        savedWord: '✓ Salvata come parola!',
        savedPhrase: '✓ Salvata come frase!',
        choose: 'Scegli una risposta',
        correct: 'Corretto!',
        notQuite: 'Non proprio. Rivedrai questa domanda.',
        xpEarned: 'XP guadagnati',
        correctLabel: 'Corrette',
        questionsLabel: 'Domande',
        back: 'Torna all’argomento →',
        continue: 'Continua →',
        startReview: 'Inizia il ripasso →',
        next: 'Avanti →',
        leaveTitle: 'Uscire dalla lezione?',
        leaveDesc: 'I progressi di questa lezione andranno persi.',
        keepGoing: 'Continua',
        leave: 'Esci',
        unavailableTitle: 'Temporaneamente non disponibile',
        unavailableBody: 'Impossibile caricare questa lezione al momento. Controlla la connessione e riprova.',
        tryAgainTitle: 'Riprova',
        tryAgainBody: 'Non siamo riusciti a caricare questa lezione. Torna indietro e prova a ricominciarla.',
    },
    es: {
        reviewTitle: 'Repaso rápido',
        reviewOne: 'Repasemos la pregunta que fallaste. Tendrás que acertarla para que esta lección desbloquee la siguiente.',
        reviewMany: 'Repasemos las {n} preguntas que fallaste. Tendrás que acertarlas todas para que esta lección desbloquee la siguiente.',
        reviewQuestion: 'Pregunta de repaso {n} de {total}',
        question: 'Pregunta {n}',
        saveTitle: 'Guardar en el Word Bank',
        hint: 'Toca 🔖 o mantén pulsado para guardar',
        hear: '▶ Escuchar',
        saveAs: 'Guardar "{word}" como...',
        saveWord: '💾 Guardar palabra',
        savePhrase: '📝 Guardar frase',
        cancel: 'Cancelar',
        savedWord: '✓ ¡Guardada como palabra!',
        savedPhrase: '✓ ¡Guardada como frase!',
        choose: 'Elige una respuesta',
        correct: '¡Correcto!',
        notQuite: 'No del todo. Volverás a ver esta pregunta.',
        xpEarned: 'XP ganados',
        correctLabel: 'Correctas',
        questionsLabel: 'Preguntas',
        back: 'Volver al tema →',
        continue: 'Continuar →',
        startReview: 'Empezar el repaso →',
        next: 'Siguiente →',
        leaveTitle: '¿Salir de esta lección?',
        leaveDesc: 'Perderás el progreso de esta lección.',
        keepGoing: 'Seguir',
        leave: 'Salir',
        unavailableTitle: 'No disponible por ahora',
        unavailableBody: 'Esta lección no se pudo cargar ahora. Revisa tu conexión e inténtalo de nuevo.',
        tryAgainTitle: 'Inténtalo de nuevo',
        tryAgainBody: 'No pudimos cargar esta lección. Vuelve atrás e intenta empezarla otra vez.',
    },
    fr: {
        reviewTitle: 'Petite révision',
        reviewOne: 'Revoyons la question que tu as ratée. Tu devras y répondre correctement avant que cette leçon débloque la suivante.',
        reviewMany: 'Revoyons les {n} questions que tu as ratées. Tu devras toutes les réussir avant que cette leçon débloque la suivante.',
        reviewQuestion: 'Question de révision {n} sur {total}',
        question: 'Question {n}',
        saveTitle: 'Ajouter au Word Bank',
        hint: 'Touche 🔖 ou maintiens appuyé pour enregistrer',
        hear: '▶ Écouter',
        saveAs: 'Enregistrer "{word}" comme...',
        saveWord: '💾 Enregistrer le mot',
        savePhrase: '📝 Enregistrer la phrase',
        cancel: 'Annuler',
        savedWord: '✓ Enregistré comme mot !',
        savedPhrase: '✓ Enregistrée comme phrase !',
        choose: 'Choisis une réponse',
        correct: 'Correct !',
        notQuite: 'Pas tout à fait. Tu reverras cette question.',
        xpEarned: 'XP gagnés',
        correctLabel: 'Correctes',
        questionsLabel: 'Questions',
        back: 'Retour au thème →',
        continue: 'Continuer →',
        startReview: 'Commencer la révision →',
        next: 'Suivant →',
        leaveTitle: 'Quitter cette leçon ?',
        leaveDesc: 'Ta progression dans cette leçon sera perdue.',
        keepGoing: 'Continuer',
        leave: 'Quitter',
        unavailableTitle: 'Temporairement indisponible',
        unavailableBody: 'Cette leçon n’a pas pu se charger pour l’instant. Vérifie ta connexion et réessaie.',
        tryAgainTitle: 'Réessaie',
        tryAgainBody: 'Nous n’avons pas pu charger cette leçon. Reviens en arrière et essaie de la relancer.',
    },
    pt: {
        reviewTitle: 'Revisão rápida',
        reviewOne: 'Vamos rever a pergunta que você errou. Você precisará acertá-la para esta lição liberar a próxima.',
        reviewMany: 'Vamos rever as {n} perguntas que você errou. Você precisará acertar todas para esta lição liberar a próxima.',
        reviewQuestion: 'Pergunta de revisão {n} de {total}',
        question: 'Pergunta {n}',
        saveTitle: 'Salvar no Word Bank',
        hint: 'Toque em 🔖 ou mantenha pressionado para salvar',
        hear: '▶ Ouvir',
        saveAs: 'Salvar "{word}" como...',
        saveWord: '💾 Salvar palavra',
        savePhrase: '📝 Salvar frase',
        cancel: 'Cancelar',
        savedWord: '✓ Salva como palavra!',
        savedPhrase: '✓ Salva como frase!',
        choose: 'Escolha uma resposta',
        correct: 'Correto!',
        notQuite: 'Quase. Você verá esta pergunta de novo.',
        xpEarned: 'XP ganhos',
        correctLabel: 'Corretas',
        questionsLabel: 'Perguntas',
        back: 'Voltar ao tema →',
        continue: 'Continuar →',
        startReview: 'Começar a revisão →',
        next: 'Próxima →',
        leaveTitle: 'Sair desta lição?',
        leaveDesc: 'Seu progresso nesta lição será perdido.',
        keepGoing: 'Continuar',
        leave: 'Sair',
        unavailableTitle: 'Temporariamente indisponível',
        unavailableBody: 'Esta lição não pôde ser carregada agora. Verifique sua conexão e tente novamente.',
        tryAgainTitle: 'Tente novamente',
        tryAgainBody: 'Não conseguimos carregar esta lição. Volte e tente iniciá-la de novo.',
    },
};

function Lesson() {
    const navigate = useNavigate();
    const { interfaceLanguage } = useLanguage();
    const { activeLanguage, activeLevel } = useUserLanguage();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const s = (key, values) => {
        let text = TEXT[uiLang][key] || TEXT.en[key];
        Object.keys(values || {}).forEach(name => {
            text = text.replace(`{${name}}`, values[name]);
        });
        return text;
    };
    const { lessonSlug } = useParams();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [correctCount, setCorrectCount] = useState(0);
    const [showAbandon, setShowAbandon] = useState(false);
    const [phase, setPhase] = useState('main');
    const [wrongQuestions, setWrongQuestions] = useState([]);
    const [reviewQueue, setReviewQueue] = useState([]);
    const [showSaveMenu, setShowSaveMenu] = useState(false);
    const [savedFeedback, setSavedFeedback] = useState(null);
    const [questionResults, setQuestionResults] = useState([]);
    const longPressTimer = useRef(null);
    const topicSlug = lessonSlug?.includes('__') ? lessonSlug.split('__')[0] : null;
    const lessonKey = lessonSlug?.includes('__') ? lessonSlug.split('__')[1] : lessonSlug;

    // Hand-written lessons: Italian lessons live in lessonsItalian.js, English lessons in lessonsEnglish.js.
    // Other languages generate their own for now.
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    let staticLesson;
    if (targetLang === 'italian') {
        const italianSet = lessonsItalian[interfaceLanguage] || lessonsItalian.en;
        staticLesson = italianSet[lessonKey];
    } else if (targetLang === 'english') {
        const englishSet = lessonsEnglish[interfaceLanguage] || lessonsEnglish.en;
        staticLesson = englishSet[lessonKey];
    }
    const allManualVersions = React.useMemo(
        () => {
            const manualVersions = lessonVersions[lessonKey] || [];
            return staticLesson ? [staticLesson, ...manualVersions] : [];
        },
        [staticLesson, lessonKey]
    );

    const [lesson, setLesson] = useState(null);
    const [lessonLoading, setLessonLoading] = useState(true);
    const [, setLessonError] = useState(false);

    useEffect(() => {
        const loadLesson = async () => {
            setLessonLoading(true);

            if (staticLesson) {
                const { data: { user } } = await supabase.auth.getUser();
                let alreadyCompleted = false;
                if (user && topicSlug && lessonKey) {
                    const { data } = await supabase
                        .from('user_progress')
                        .select('id')
                        .eq('user_id', user.id)
                        .eq('topic_slug', topicSlug)
                        .eq('lesson_slug', lessonKey)
                        .eq('target_language', targetLang)
                        .maybeSingle();
                    alreadyCompleted = !!data;
                }

                if (!alreadyCompleted) {
                    setLesson(staticLesson);
                    setLessonLoading(false);
                    return;
                }

                const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
                const language = targetLang;
                const lessonTitle = lessonKey?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Lesson';
                const topicTitle = topicSlug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Italian';

                try {
                    const { data: cachedVersions } = await supabase
                        .from('lesson_cache')
                        .select('content, version')
                        .eq('topic_slug', topicSlug)
                        .eq('lesson_slug', lessonKey)
                        .eq('language', language.toLowerCase())
                        .eq('level', level)
                        .eq('interface_language', interfaceLanguage || 'en')
                        .order('version', { ascending: true });

                    const aiVersions = cachedVersions || [];
                    const combinedPool = [...allManualVersions.slice(1), ...aiVersions.map(v => v.content)];
                    const totalVersions = allManualVersions.length + aiVersions.length;

                    if (totalVersions < 1) {
                        fetch('/api/generate-lesson', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                topicSlug,
                                lessonSlug: lessonKey,
                                lessonTitle: staticLesson?.title || lessonTitle,
                                topicName: staticLesson?.topic || topicTitle,
                                language: language.toLowerCase(),
                                level,
                                existingVersionCount: totalVersions,
                                interfaceLanguage: interfaceLanguage || 'en',
                            }),
                        })
                            .then(r => r.json())
                            .then(d => console.log('Background generation result:', d))
                            .catch(err => console.error('Background generation error:', err));
                    }

                    if (combinedPool.length > 0) {
                        const pick = combinedPool[Math.floor(Math.random() * combinedPool.length)];
                        setLesson(pick);
                    } else {
                        setLesson(staticLesson);
                    }
                } catch (err) {
                    setLesson(staticLesson);
                }
                setLessonLoading(false);
                return;
            }

            try {
                const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
                const language = targetLang;
                const lessonTitle = lessonKey?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Lesson';
                const topicTitle = topicSlug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Italian';
                const response = await fetch('/api/generate-lesson', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        topicSlug,
                        lessonSlug: lessonKey,
                        lessonTitle,
                        topicName: topicTitle,
                        language: language.toLowerCase(),
                        level,
                        interfaceLanguage: interfaceLanguage || 'en',
                    }),
                });
                if (!response.ok) throw new Error('Failed to generate');
                const data = await response.json();
                setLesson(data.content);
            } catch (err) {
                setLessonError(true);
                setLesson({
                    title: lessonKey?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Lesson',
                    topic: targetLang.charAt(0).toUpperCase() + targetLang.slice(1),
                    duration: '5 min',
                    xp: 15,
                    slides: [
                        { type: 'intro', title: TEXT[uiLang].unavailableTitle, body: TEXT[uiLang].unavailableBody, emoji: '⚠️' },
                        { type: 'complete', title: TEXT[uiLang].tryAgainTitle, body: TEXT[uiLang].tryAgainBody, xp: 0 },
                    ],
                });
            }
            setLessonLoading(false);
        };
        loadLesson();
    }, [lessonKey, topicSlug, staticLesson, allManualVersions, targetLang, interfaceLanguage, uiLang, activeLevel]);

    const saveProgress = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !topicSlug || !lessonKey) return;
        const totalQuestions = lesson ? lesson.slides.filter(s => s.type === 'question').length : 0;
        awardXP(lesson?.xp || 15, 'lesson');
        await Promise.all([
            supabase.from('user_progress').upsert({
                user_id: user.id,
                topic_slug: topicSlug,
                lesson_slug: lessonKey,
                target_language: targetLang,
            }, { onConflict: 'user_id,topic_slug,lesson_slug,target_language' }),
            supabase.from('lesson_results').upsert({
                user_id: user.id,
                topic_slug: topicSlug,
                lesson_slug: lessonKey,
                target_language: targetLang,
                completed_at: new Date().toISOString(),
                correct_count: correctCount,
                total_questions: totalQuestions,
                questions: questionResults,
            }, { onConflict: 'user_id,topic_slug,lesson_slug,target_language' }),
        ]);
    };

    const handlePressStart = () => {
        longPressTimer.current = setTimeout(() => {
            setShowSaveMenu(true);
        }, 500);
    };

    const handlePressEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const saveVocabWord = async (wordType) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word: slide.word,
            translation: slide.translation,
            phonetic: slide.phonetic,
            note: slide.note,
            entry_type: wordType,
            source: 'lesson',
            topic_slug: topicSlug,
            target_language: targetLang,
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });
        setShowSaveMenu(false);
        setSavedFeedback(wordType);
        setTimeout(() => setSavedFeedback(null), 1500);
    };

    const totalSlides = lesson ? lesson.slides.length : 0;

    useEffect(() => {
        if (phase === 'review' && reviewQueue.length === 0 && totalSlides > 0) {
            setCurrentSlide(totalSlides - 1);
            setPhase('complete');
        }
    }, [phase, reviewQueue, totalSlides]);

    if (lessonLoading || !lesson) {
        return (
            <div className="lesson-screen">
                <div className="lesson-top">
                    <button className="lesson-close" onClick={() => navigate(-1)}>✕</button>
                    <div className="lesson-progress-bar">
                        <div className="lesson-progress-fill" style={{ width: '0%' }}></div>
                    </div>
                    <div className="lesson-xp">XP</div>
                </div>
                <LunaLoader kind="lesson" />
            </div>
        );
    }

    let slide;
    if (phase === 'review-intro') {
        slide = {
            type: 'intro',
            title: s('reviewTitle'),
            body: wrongQuestions.length === 1 ? s('reviewOne') : s('reviewMany', { n: wrongQuestions.length }),
            emoji: '🔁',
        };
    } else if (phase === 'review' && reviewQueue.length > 0) {
        slide = reviewQueue[0];
    } else if (phase === 'complete' || (phase === 'review' && reviewQueue.length === 0)) {
        slide = lesson.slides[totalSlides - 1];
    } else {
        slide = lesson.slides[currentSlide];
    }

    let progress;
    if (phase === 'main') {
        progress = (currentSlide / (totalSlides - 1)) * 100;
    } else if (phase === 'review-intro') {
        progress = 92;
    } else if (phase === 'review') {
        const initial = wrongQuestions.length || 1;
        progress = 92 + ((initial - reviewQueue.length) / initial) * 8;
    } else {
        progress = 100;
    }

    const handleNext = () => {
        if (phase === 'review') {
            const wasCorrect = selectedAnswer === slide.correct;
            setReviewQueue(prevQueue => {
                const [first, ...rest] = prevQueue;
                return wasCorrect ? rest : [...rest, first];
            });
            setSelectedAnswer(null);
            setShowResult(false);
            return;
        }

        setSelectedAnswer(null);
        setShowResult(false);

        if (phase === 'main') {
            const nextIndex = currentSlide + 1;
            if (nextIndex === totalSlides - 1) {
                if (wrongQuestions.length > 0) {
                    setPhase('review-intro');
                } else {
                    setCurrentSlide(nextIndex);
                    setPhase('complete');
                }
            } else if (currentSlide < totalSlides - 1) {
                setCurrentSlide(nextIndex);
            }
        } else if (phase === 'review-intro') {
            setReviewQueue([...wrongQuestions]);
            setPhase('review');
        }
    };

    const handleAnswer = (index) => {
        if (showResult) return;
        setSelectedAnswer(index);
        setShowResult(true);
        const isCorrect = index === slide.correct;
        if (isCorrect) {
            setCorrectCount(c => c + 1);
        } else if (phase === 'main') {
            setWrongQuestions(prev => [...prev, slide]);
        }
        if (phase === 'main') {
            setQuestionResults(prev => [...prev, {
                question: slide.question,
                options: slide.options,
                correct: slide.correct,
                chosen: index,
                isCorrect,
                explanation: slide.explanation,
            }]);
        }
    };

    const getAnswerStyle = (index) => {
        if (!showResult) return selectedAnswer === index ? 'selected' : '';
        if (index === slide.correct) return 'correct';
        if (index === selectedAnswer && index !== slide.correct) return 'wrong';
        return 'faded';
    };

    const questionLabel = phase === 'review'
        ? s('reviewQuestion', { n: wrongQuestions.length - reviewQueue.length + 1, total: wrongQuestions.length })
        : s('question', { n: lesson.slides.slice(0, currentSlide + 1).filter(slideItem => slideItem.type === 'question').length });

    return (
        <div className="lesson-screen">

            <div className="lesson-top">
                <button className="lesson-close" onClick={() => setShowAbandon(true)}>✕</button>
                <div className="lesson-progress-bar">
                    <div className="lesson-progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <div className="lesson-xp">+{lesson.xp} XP</div>
            </div>

            <div className="lesson-content">

                {slide.type === 'intro' && (
                    <div className="lesson-intro">
                        <div className="lesson-intro-emoji">{slide.emoji}</div>
                        <div className="lesson-intro-title">{slide.title}</div>
                        <div className="lesson-intro-body">{slide.body}</div>
                    </div>
                )}

                {slide.type === 'vocabulary' && (
                    <div className="lesson-vocab">
                        <div
                            className="lesson-vocab-pressable"
                            onMouseDown={handlePressStart}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={handlePressStart}
                            onTouchEnd={handlePressEnd}
                        >
                            <button
                                className="lesson-vocab-bookmark"
                                onClick={(e) => { e.stopPropagation(); setShowSaveMenu(true); }}
                                title={s('saveTitle')}
                            >
                                🔖
                            </button>
                            <div className="lesson-vocab-emoji">{slide.emoji}</div>
                            <div className="lesson-vocab-word">{slide.word}</div>
                            <div className="lesson-vocab-phonetic">{slide.phonetic}</div>
                            <div className="lesson-vocab-translation">{slide.translation}</div>
                            <div className="lesson-vocab-note">{slide.note}</div>
                        </div>
                        <div className="lesson-vocab-hint">{s('hint')}</div>
                        <button className="lesson-play-btn" onClick={() => speakText(slide.word, undefined, targetLang)}>{s('hear')}</button>

                        {showSaveMenu && (
                            <div className="lesson-save-overlay" onClick={() => setShowSaveMenu(false)}>
                                <div className="lesson-save-menu" onClick={e => e.stopPropagation()}>
                                    <div className="lesson-save-menu-title">{s('saveAs', { word: slide.word })}</div>
                                    <button className="lesson-save-menu-btn" onClick={() => saveVocabWord('word')}>
                                        {s('saveWord')}
                                    </button>
                                    <button className="lesson-save-menu-btn" onClick={() => saveVocabWord('phrase')}>
                                        {s('savePhrase')}
                                    </button>
                                    <button className="lesson-save-menu-cancel" onClick={() => setShowSaveMenu(false)}>
                                        {s('cancel')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {savedFeedback && (
                            <div className="lesson-save-toast">
                                {savedFeedback === 'word' ? s('savedWord') : s('savedPhrase')}
                            </div>
                        )}
                    </div>
                )}

                {slide.type === 'question' && (
                    <div className="lesson-question">
                        <div className="lesson-question-num">{questionLabel}</div>
                        <div className="lesson-question-text">{slide.question}</div>
                        <div className="lesson-options">
                            {slide.options.map((option, index) => (
                                <div
                                    key={index}
                                    className={`lesson-option ${getAnswerStyle(index)}`}
                                    onClick={() => handleAnswer(index)}
                                >
                                    <div className={`lesson-option-letter ${getAnswerStyle(index)}`}>
                                        {['A', 'B', 'C', 'D'][index]}
                                    </div>
                                    <div className="lesson-option-text">{option}</div>
                                </div>
                            ))}
                        </div>
                        {showResult && (
                            <div className={`lesson-result ${selectedAnswer === slide.correct ? 'correct' : 'wrong'}`}>
                                <div className="lesson-result-icon">
                                    {selectedAnswer === slide.correct ? '✓' : '✕'}
                                </div>
                                <div className="lesson-result-text">
                                    <div className="lesson-result-title">
                                        {selectedAnswer === slide.correct ? s('correct') : s('notQuite')}
                                    </div>
                                    <div className="lesson-result-explanation">{slide.explanation}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {slide.type === 'complete' && (
                    <div className="lesson-complete">
                        <div className="lesson-complete-icon">🎉</div>
                        <div className="lesson-complete-title">{slide.title}</div>
                        <div className="lesson-complete-body">{slide.body}</div>
                        <div className="lesson-complete-stats">
                            <div className="lesson-complete-stat">
                                <div className="lesson-complete-stat-value">+{slide.xp}</div>
                                <div className="lesson-complete-stat-label">{s('xpEarned')}</div>
                            </div>
                            <div className="lesson-complete-stat">
                                <div className="lesson-complete-stat-value">{correctCount}</div>
                                <div className="lesson-complete-stat-label">{s('correctLabel')}</div>
                            </div>
                            <div className="lesson-complete-stat">
                                <div className="lesson-complete-stat-value">
                                    {lesson.slides.filter(slideItem => slideItem.type === 'question').length}
                                </div>
                                <div className="lesson-complete-stat-label">{s('questionsLabel')}</div>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            <div className="lesson-footer">
                {slide.type === 'question' && !showResult ? (
                    <button className="lesson-btn" disabled>
                        {s('choose')}
                    </button>
                ) : slide.type === 'complete' ? (
                    <button className="lesson-btn" onClick={async () => { await saveProgress(); navigate(-1); }}>
                        {s('back')}
                    </button>
                ) : (
                    <button className="lesson-btn" onClick={handleNext}>
                        {slide.type === 'question' ? s('continue') : phase === 'review-intro' ? s('startReview') : s('next')}
                    </button>
                )}
            </div>

            {showAbandon && (
                <div className="lesson-abandon-overlay">
                    <div className="lesson-abandon-card">
                        <div className="lesson-abandon-title">{s('leaveTitle')}</div>
                        <div className="lesson-abandon-desc">
                            {s('leaveDesc')}
                        </div>
                        <div className="lesson-abandon-actions">
                            <button className="lesson-abandon-cancel" onClick={() => setShowAbandon(false)}>
                                {s('keepGoing')}
                            </button>
                            <button className="lesson-abandon-confirm" onClick={() => navigate(-1)}>
                                {s('leave')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default Lesson;